/**
 * DistributionQueues - Tela de gerenciamento de filas de distribuição global
 * 
 * Permite criar, editar e gerenciar filas de distribuição que podem ser
 * usadas tanto para conversas (chat) quanto para deals (CRM).
 * 
 * Funcionalidades:
 * - Listagem de filas com métricas (membros, online, última distribuição)
 * - Criação de novas filas com configuração de método e limites
 * - Gerenciamento de membros da fila
 * - Ativação/desativação de filas
 * 
 * @version 1.0.0
 * @date 2026-01-20
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// Ícones
import {
  Activity,
  Plus,
  Users,
  Settings2,
  Trash2,
  Edit2,
  RefreshCw,
  Layers,
  MessageSquare,
  Briefcase,
  Globe,
  CheckCircle,
  XCircle,
  UserPlus,
  RotateCw,
  LayoutGrid,
  List,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

// Componentes UI
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

// Logger para rastreamento
const logger = createLogger('DistributionQueues');

// ==============================================================
// Types
// ==============================================================

/** Tipo para modo de visualização */
type ViewMode = 'cards' | 'list';

// ==============================================================
// Interfaces
// ==============================================================

/** Fila de distribuição com estatísticas */
interface DistributionQueue {
  id: string;
  name: string;
  description: string | null;
  context_type: 'chat' | 'crm' | 'all';
  target_type: 'department' | 'workspace' | 'pipeline' | 'global';
  target_id: string | null;
  distribution_method: string;
  max_items_per_agent: number;
  is_active: boolean;
  created_at: string;
  members_count: number;
  online_members_count: number;
}

/** Membro de fila */
interface QueueMember {
  user_id: string;
  is_active: boolean;
  priority: number;
  profile: {
    full_name: string;
    email: string;
    avatar_url: string | null;
  };
  online_status?: string;
}

/** Agente disponível para adicionar */
interface AvailableAgent {
  user_id: string;
  profile: {
    full_name: string;
    email: string;
    avatar_url: string | null;
  };
}

// ==============================================================
// Componente Principal
// ==============================================================

export default function DistributionQueues() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  // Estados do dialog
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isMembersDialogOpen, setIsMembersDialogOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState<DistributionQueue | null>(null);

  // Estado do modo de visualização com persistência em localStorage
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('distribution_queues_viewMode');
    return (saved === 'cards' || saved === 'list') ? saved : 'cards';
  });

  /** Handler para alternar modo de visualização */
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem('distribution_queues_viewMode', mode);
  };

  // Estado do formulário
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    context_type: 'all' as 'chat' | 'crm' | 'all',
    distribution_method: 'round_robin',
    max_items_per_agent: 10,
  });

  // ==============================================================
  // Queries
  // ==============================================================

  /**
   * Busca filas de distribuição do tenant via RPC server-side.
   * v2.0.0: get_distribution_queues_with_stats consolida lista + members_count
   * + online_members_count em UMA chamada (resolve N+1 anterior).
   */
  const { data: queues, isLoading, refetch } = useQuery({
    queryKey: ['distribution-queues', tenantId],
    queryFn: async (): Promise<DistributionQueue[]> => {
      if (!tenantId) return [];

      logger.info('Buscando filas de distribuição (RPC consolidada)');

      const { data, error } = await supabase.rpc('get_distribution_queues_with_stats', {
        p_tenant_hint: tenantId,
      });

      if (error) {
        logger.error('Erro ao carregar filas via RPC', error);
        throw error;
      }

      const list: DistributionQueue[] = (data || []).map((q: any) => ({
        id: q.id,
        name: q.name,
        description: q.description,
        context_type: q.context_type,
        target_type: q.target_type,
        target_id: q.target_id,
        distribution_method: q.distribution_method,
        max_items_per_agent: q.max_items_per_agent,
        is_active: q.is_active,
        created_at: q.created_at,
        members_count: q.members_count ?? 0,
        online_members_count: q.online_members_count ?? 0,
      }));

      logger.info('Filas carregadas', { count: list.length });
      return list;
    },
    enabled: !!tenantId,
    refetchInterval: 30000, // mantém fallback para refletir status online
  });

  /** Busca membros de uma fila específica via RPC */
  const { data: queueMembers, isLoading: isMembersLoading } = useQuery({
    queryKey: ['queue-members', selectedQueue?.id],
    queryFn: async (): Promise<QueueMember[]> => {
      if (!selectedQueue?.id || !tenantId) return [];

      // Chama RPC que retorna membros com dados completos e status online
      const { data, error } = await supabase.rpc('get_distribution_queue_members', {
        p_queue_id: selectedQueue.id,
        p_tenant_hint: tenantId
      });

      if (error) {
        logger.error('Erro ao buscar membros da fila', { error });
        throw error;
      }

      // Mapeia para o formato esperado pelo componente
      return (data || []).map((m: any) => ({
        user_id: m.user_id,
        is_active: m.is_active,
        priority: m.weight || 1,
        profile: {
          full_name: m.full_name,
          email: m.email,
          avatar_url: m.avatar_url,
        },
        online_status: m.online_status || 'offline',
      }));
    },
    enabled: !!selectedQueue?.id && isMembersDialogOpen,
  });

  /**
   * Busca agentes disponíveis para adicionar à fila.
   * Reaproveita queryData de queue-members (já obtido via RPC) para descobrir
   * quem já está dentro, evitando 1 round-trip extra a distribution_queue_members.
   */
  const { data: availableAgents } = useQuery({
    queryKey: ['available-agents', selectedQueue?.id, tenantId, queueMembers?.length || 0],
    queryFn: async (): Promise<AvailableAgent[]> => {
      if (!selectedQueue?.id || !tenantId) return [];

      const currentIds = new Set((queueMembers || []).map(m => m.user_id));

      // RPC server-side: lista membros ativos do tenant (cache compartilhado)
      const { data: allAgents, error } = await (supabase.rpc as any)(
        'get_tenant_members_safe',
        { p_tenant_hint: tenantId, p_only_active: true }
      );

      if (error) throw error;

      return ((allAgents || []) as any[])
        .filter(a => !currentIds.has(a.user_id))
        .map(a => ({
          user_id: a.user_id,
          profile: {
            full_name: a.full_name,
            email: a.email,
            avatar_url: a.avatar_url,
          } as any,
        }));
    },
    enabled: !!selectedQueue?.id && isMembersDialogOpen,
  });

  // ==============================================================
  // Mutations
  // ==============================================================

  /** Cria nova fila via RPC server-side (valida tenant + nome obrigatório) */
  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      if (!tenantId) throw new Error('Tenant não encontrado');

      const { error } = await supabase.rpc('create_distribution_queue', {
        p_name: data.name,
        p_description: data.description || null,
        p_context_type: data.context_type,
        p_distribution_method: data.distribution_method,
        p_max_items_per_agent: data.max_items_per_agent,
        p_tenant_hint: tenantId,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution-queues'] });
      setIsCreateDialogOpen(false);
      resetForm();
      toast.success('Fila criada com sucesso!');
    },
    onError: (error: any) => {
      logger.error('Erro ao criar fila', error);
      toast.error(error.message || 'Erro ao criar fila');
    },
  });

  /** Atualiza fila via RPC server-side (valida ownership por tenant) */
  const updateMutation = useMutation({
    mutationFn: async (data: typeof formData & { id: string }) => {
      const { error } = await supabase.rpc('update_distribution_queue', {
        p_queue_id: data.id,
        p_name: data.name,
        p_description: data.description || null,
        p_context_type: data.context_type,
        p_distribution_method: data.distribution_method,
        p_max_items_per_agent: data.max_items_per_agent,
        p_tenant_hint: tenantId,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution-queues'] });
      setIsEditDialogOpen(false);
      setSelectedQueue(null);
      resetForm();
      toast.success('Fila atualizada com sucesso!');
    },
    onError: (error: any) => {
      logger.error('Erro ao atualizar fila', error);
      toast.error(error.message || 'Erro ao atualizar fila');
    },
  });

  /** Exclui fila + membros atomicamente via RPC */
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc('delete_distribution_queue', {
        p_queue_id: id,
        p_tenant_hint: tenantId,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution-queues'] });
      setIsDeleteDialogOpen(false);
      setSelectedQueue(null);
      toast.success('Fila excluída com sucesso!');
    },
    onError: (error: any) => {
      logger.error('Erro ao excluir fila', error);
      toast.error(error.message || 'Erro ao excluir fila');
    },
  });

  /** Toggle ativa/inativa via RPC */
  const toggleStatusMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase.rpc('toggle_distribution_queue_active', {
        p_queue_id: id,
        p_is_active: isActive,
        p_tenant_hint: tenantId,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution-queues'] });
      toast.success('Status atualizado!');
    },
  });

  /** Adiciona membro via RPC (valida que o user é membro ativo do tenant) */
  const addMemberMutation = useMutation({
    mutationFn: async ({ queueId, userId }: { queueId: string; userId: string }) => {
      if (!tenantId) throw new Error('Tenant não encontrado');

      const { error } = await supabase.rpc('add_distribution_queue_member', {
        p_queue_id: queueId,
        p_user_id: userId,
        p_tenant_hint: tenantId,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queue-members'] });
      queryClient.invalidateQueries({ queryKey: ['available-agents'] });
      queryClient.invalidateQueries({ queryKey: ['distribution-queues'] });
      toast.success('Membro adicionado!');
    },
  });

  /** Remove membro via RPC (valida ownership da fila) */
  const removeMemberMutation = useMutation({
    mutationFn: async ({ queueId, userId }: { queueId: string; userId: string }) => {
      const { error } = await supabase.rpc('remove_distribution_queue_member', {
        p_queue_id: queueId,
        p_user_id: userId,
        p_tenant_hint: tenantId,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queue-members'] });
      queryClient.invalidateQueries({ queryKey: ['available-agents'] });
      queryClient.invalidateQueries({ queryKey: ['distribution-queues'] });
      toast.success('Membro removido!');
    },
  });

  // ==============================================================
  // Handlers
  // ==============================================================

  /** Reseta formulário */
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      context_type: 'all',
      distribution_method: 'round_robin',
      max_items_per_agent: 10,
    });
  };

  /** Abre dialog de edição */
  const handleEdit = (queue: DistributionQueue) => {
    setSelectedQueue(queue);
    setFormData({
      name: queue.name,
      description: queue.description || '',
      context_type: queue.context_type,
      distribution_method: queue.distribution_method,
      max_items_per_agent: queue.max_items_per_agent,
    });
    setIsEditDialogOpen(true);
  };

  /** Abre dialog de membros */
  const handleManageMembers = (queue: DistributionQueue) => {
    setSelectedQueue(queue);
    setIsMembersDialogOpen(true);
  };

  /** Retorna ícone baseado no contexto */
  const getContextIcon = (context: string) => {
    switch (context) {
      case 'chat':
        return <MessageSquare className="h-4 w-4 text-emerald-500" />;
      case 'crm':
        return <Briefcase className="h-4 w-4 text-blue-500" />;
      default:
        return <Globe className="h-4 w-4 text-purple-500" />;
    }
  };

  /** Retorna label do contexto */
  const getContextLabel = (context: string) => {
    switch (context) {
      case 'chat':
        return 'Conversas';
      case 'crm':
        return 'CRM';
      default:
        return 'Global';
    }
  };

  // ==============================================================
  // Render
  // ==============================================================

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header Premium */}
      {/* Cabeçalho - Padronizado com Fila de Atendimento */}
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold text-foreground truncate">
            Filas de Distribuição
          </h1>
          <p className="hidden sm:block text-sm text-muted-foreground mt-1">
            Gerencie filas globais de distribuição automática
          </p>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          {/* Toggle de visualização (cards/lista) */}
          <div className="flex items-center bg-muted/50 rounded-lg p-1 border border-border">
            <button
              onClick={() => handleViewModeChange('cards')}
              className={cn(
                'flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                viewMode === 'cards'
                  ? 'bg-foreground text-background shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              title="Visualizar em cards"
            >
              <LayoutGrid className="h-4 w-4" />
              <span className="hidden sm:inline">Cards</span>
            </button>
            <button
              onClick={() => handleViewModeChange('list')}
              className={cn(
                'flex items-center gap-1.5 px-2 sm:px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                viewMode === 'list'
                  ? 'bg-foreground text-background shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              title="Visualizar em lista"
            >
              <List className="h-4 w-4" />
              <span className="hidden sm:inline">Lista</span>
            </button>
          </div>

          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 sm:w-auto sm:px-3"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
            <span className="hidden sm:inline ml-1.5">Atualizar</span>
          </Button>

          <Button
            size="sm"
            onClick={() => setIsCreateDialogOpen(true)}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            <span className="hidden sm:inline">Nova Fila</span>
          </Button>
        </div>
      </div>

      {/* Lista de Filas */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="overflow-hidden">
              <CardHeader className="pb-3">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-48 mt-2" />
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-8 w-full" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : queues?.length === 0 ? (
        <Card className="p-12 text-center">
          <Layers className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Nenhuma fila criada</h3>
          <p className="text-muted-foreground mb-4">
            Crie sua primeira fila de distribuição para gerenciar atribuições automáticas.
          </p>
          <Button 
            size="xs"
            onClick={() => setIsCreateDialogOpen(true)} 
            className="transition-all duration-200 hover:shadow-md hover:scale-[1.02]"
          >
            <Plus className="mr-1.5" />
            Criar Primeira Fila
          </Button>
        </Card>
      ) : viewMode === 'cards' ? (
        // ==============================================================
        // Visualização em Cards (Grid)
        // ==============================================================
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {queues?.map((queue) => (
            <Card
              key={queue.id}
              className={cn(
                'group relative overflow-hidden transition-all duration-300 hover:shadow-lg',
                !queue.is_active && 'opacity-60'
              )}
            >
              {/* Shimmer effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />

              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    {getContextIcon(queue.context_type)}
                    <CardTitle className="text-base">{queue.name}</CardTitle>
                  </div>

                  {/* Toggle ativar/desativar — padrão icon-button (igual Departamentos) */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() =>
                          toggleStatusMutation.mutate({
                            id: queue.id,
                            isActive: !queue.is_active,
                          })
                        }
                      >
                        {queue.is_active ? (
                          <ToggleRight className="h-4 w-4 text-emerald-600" />
                        ) : (
                          <ToggleLeft className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {queue.is_active ? 'Desativar' : 'Ativar'}
                    </TooltipContent>
                  </Tooltip>
                </div>

                {queue.description && (
                  <CardDescription className="line-clamp-2">
                    {queue.description}
                  </CardDescription>
                )}
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Badges de info */}
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="gap-1">
                    {getContextIcon(queue.context_type)}
                    {getContextLabel(queue.context_type)}
                  </Badge>

                  <Badge variant="secondary" className="gap-1">
                    <Activity className="h-3 w-3" />
                    {queue.distribution_method === 'round_robin'
                      ? 'Round Robin'
                      : 'Menos Ocupado'}
                  </Badge>
                </div>

                {/* Métricas */}
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1.5">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{queue.members_count}</span>
                    <span className="text-muted-foreground">membros</span>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <span
                      className={cn(
                        'h-2 w-2 rounded-full',
                        queue.online_members_count > 0 ? 'bg-emerald-500' : 'bg-muted'
                      )}
                    />
                    <span className="font-medium">{queue.online_members_count}</span>
                    <span className="text-muted-foreground">online</span>
                  </div>
                </div>

                {/* Limite por agente - exibe "ativos" e suporta valor 0 */}
                <p className="text-xs text-muted-foreground">
                  {queue.max_items_per_agent === 0 
                    ? 'Sem limite por agente'
                    : queue.context_type === 'crm'
                      ? `Máx: ${queue.max_items_per_agent} negócios ativos/agente`
                      : queue.context_type === 'chat'
                        ? `Máx: ${queue.max_items_per_agent} conversas ativas/agente`
                        : `Máx: ${queue.max_items_per_agent} itens ativos/agente`
                  }
                </p>

                {/* Ações */}
                <div className="flex items-center gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="xs"
                    className="flex-1"
                    onClick={() => handleManageMembers(queue)}
                  >
                    <Users className="mr-1.5" />
                    Membros
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => handleEdit(queue)}
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={() => {
                      setSelectedQueue(queue);
                      setIsDeleteDialogOpen(true);
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        // ==============================================================
        // Visualização em Lista (Tabela)
        // ==============================================================
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">Status</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead className="hidden md:table-cell">Contexto</TableHead>
                  <TableHead className="hidden sm:table-cell">Método</TableHead>
                  <TableHead className="text-center">Membros</TableHead>
                  <TableHead className="hidden lg:table-cell text-center">Online</TableHead>
                  <TableHead className="hidden lg:table-cell">Limite</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {queues?.map((queue) => (
                  <TableRow
                    key={queue.id}
                    className={cn(
                      'group transition-colors',
                      !queue.is_active && 'opacity-60'
                    )}
                  >
                    {/* Status */}
                    {/* Status — toggle ativar/desativar padrão Departamentos */}
                    <TableCell>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() =>
                              toggleStatusMutation.mutate({
                                id: queue.id,
                                isActive: !queue.is_active,
                              })
                            }
                          >
                            {queue.is_active ? (
                              <ToggleRight className="h-4 w-4 text-emerald-600" />
                            ) : (
                              <ToggleLeft className="h-4 w-4 text-muted-foreground" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          {queue.is_active ? 'Desativar' : 'Ativar'}
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>

                    {/* Nome */}
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getContextIcon(queue.context_type)}
                        <div>
                          <p className="font-medium">{queue.name}</p>
                          {queue.description && (
                            <p className="text-xs text-muted-foreground line-clamp-1 max-w-[200px]">
                              {queue.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>

                    {/* Contexto */}
                    <TableCell className="hidden md:table-cell">
                      <Badge variant="outline" className="gap-1">
                        {getContextIcon(queue.context_type)}
                        {getContextLabel(queue.context_type)}
                      </Badge>
                    </TableCell>

                    {/* Método */}
                    <TableCell className="hidden sm:table-cell">
                      <Badge variant="secondary" className="gap-1">
                        <Activity className="h-3 w-3" />
                        {queue.distribution_method === 'round_robin'
                          ? 'Round Robin'
                          : 'Menos Ocupado'}
                      </Badge>
                    </TableCell>

                    {/* Membros */}
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{queue.members_count}</span>
                      </div>
                    </TableCell>

                    {/* Online */}
                    <TableCell className="hidden lg:table-cell text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <span
                          className={cn(
                            'h-2 w-2 rounded-full',
                            queue.online_members_count > 0 ? 'bg-emerald-500' : 'bg-muted'
                          )}
                        />
                        <span className="font-medium">{queue.online_members_count}</span>
                      </div>
                    </TableCell>

                    {/* Limite - exibe "ativos" e suporta valor 0 */}
                    <TableCell className="hidden lg:table-cell">
                      <span className="text-sm text-muted-foreground">
                        {queue.max_items_per_agent === 0 
                          ? 'Ilimitado' 
                          : `${queue.max_items_per_agent} ativos/agente`
                        }
                      </span>
                    </TableCell>

                    {/* Ações */}
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => handleManageMembers(queue)}
                          className="hidden sm:flex"
                        >
                          <Users className="h-3.5 w-3.5 mr-1" />
                          Membros
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 sm:hidden"
                          onClick={() => handleManageMembers(queue)}
                        >
                          <Users className="h-3.5 w-3.5" />
                        </Button>

                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => handleEdit(queue)}
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>

                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => {
                            setSelectedQueue(queue);
                            setIsDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Dialog de Criação/Edição */}
      <Dialog
        open={isCreateDialogOpen || isEditDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsCreateDialogOpen(false);
            setIsEditDialogOpen(false);
            setSelectedQueue(null);
            resetForm();
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {isEditDialogOpen ? 'Editar Fila' : 'Nova Fila de Distribuição'}
            </DialogTitle>
            <DialogDescription>
              {isEditDialogOpen
                ? 'Atualize as configurações da fila'
                : 'Configure uma nova fila de distribuição automática'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Nome */}
            <div className="space-y-2">
              <Label htmlFor="name">Nome da Fila *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Fila de Vendas"
              />
            </div>

            {/* Descrição */}
            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição opcional da fila"
                rows={2}
              />
            </div>

            {/* Contexto */}
            <div className="space-y-2">
              <Label>Contexto</Label>
              <Select
                value={formData.context_type}
                onValueChange={(value: 'chat' | 'crm' | 'all') =>
                  setFormData({ ...formData, context_type: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-purple-500" />
                      Global (Chat + CRM)
                    </div>
                  </SelectItem>
                  <SelectItem value="chat">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-emerald-500" />
                      Apenas Conversas
                    </div>
                  </SelectItem>
                  <SelectItem value="crm">
                    <div className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-blue-500" />
                      Apenas CRM (Deals)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Método de distribuição */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <RotateCw className="h-4 w-4" />
                Método de Distribuição
              </Label>
              <Select
                value={formData.distribution_method}
                onValueChange={(value) =>
                  setFormData({ ...formData, distribution_method: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="round_robin">
                    <div className="flex flex-col">
                      <span>Rodízio (Round Robin)</span>
                      <span className="text-xs text-muted-foreground">
                        Distribui em sequência para cada agente
                      </span>
                    </div>
                  </SelectItem>
                  <SelectItem value="least_busy">
                    <div className="flex flex-col">
                      <span>Menos Ocupado</span>
                      <span className="text-xs text-muted-foreground">
                        Atribui ao agente com menos itens ativos
                      </span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Limite por agente - permite zero (sem limite) e descreve dinamicamente */}
            <div className="space-y-2">
              <Label htmlFor="max_items">Máximo de Itens Ativos por Agente</Label>
              <Input
                id="max_items"
                type="number"
                min={0}
                max={100}
                value={formData.max_items_per_agent}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  setFormData({ 
                    ...formData, 
                    max_items_per_agent: isNaN(value) ? 10 : value 
                  });
                }}
              />
              <p className="text-xs text-muted-foreground">
                {formData.max_items_per_agent === 0 
                  ? 'Sem limite - agentes podem receber itens ilimitados'
                  : formData.context_type === 'crm' 
                    ? `Limite de ${formData.max_items_per_agent} negócios ativos (não finalizados) por agente`
                    : formData.context_type === 'chat'
                      ? `Limite de ${formData.max_items_per_agent} conversas ativas (não fechadas) por agente`
                      : `Limite de ${formData.max_items_per_agent} itens ativos (conversas + negócios) por agente`
                }
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsCreateDialogOpen(false);
                setIsEditDialogOpen(false);
                resetForm();
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (isEditDialogOpen && selectedQueue) {
                  updateMutation.mutate({ ...formData, id: selectedQueue.id });
                } else {
                  createMutation.mutate(formData);
                }
              }}
              disabled={!formData.name || createMutation.isPending || updateMutation.isPending}
            >
              {isEditDialogOpen ? 'Salvar' : 'Criar Fila'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Exclusão */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Fila</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a fila "{selectedQueue?.name}"? Esta ação não pode ser
              desfeita e todos os membros serão removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => selectedQueue && deleteMutation.mutate(selectedQueue.id)}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de Membros */}
      <Dialog open={isMembersDialogOpen} onOpenChange={setIsMembersDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Membros da Fila</DialogTitle>
            <DialogDescription>
              Gerencie os agentes que participam da fila "{selectedQueue?.name}"
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Membros atuais */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Membros Atuais ({queueMembers?.length || 0})
              </Label>

              {isMembersLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : queueMembers?.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  Nenhum membro adicionado ainda
                </p>
              ) : (
                <ScrollArea className="h-[200px]">
                  <div className="space-y-2">
                    {queueMembers?.map((member) => (
                      <div
                        key={member.user_id}
                        className="flex items-center justify-between p-2 rounded-lg border bg-muted/30"
                      >
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={member.profile.avatar_url || undefined} />
                              <AvatarFallback>
                                {member.profile.full_name?.charAt(0).toUpperCase() || 'U'}
                              </AvatarFallback>
                            </Avatar>
                            <span
                              className={cn(
                                'absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background',
                                member.online_status === 'online'
                                  ? 'bg-emerald-500'
                                  : 'bg-muted-foreground/50'
                              )}
                            />
                          </div>

                          <div>
                            <p className="text-sm font-medium">{member.profile.full_name}</p>
                            <p className="text-xs text-muted-foreground">{member.profile.email}</p>
                          </div>
                        </div>

                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() =>
                            selectedQueue &&
                            removeMemberMutation.mutate({
                              queueId: selectedQueue.id,
                              userId: member.user_id,
                            })
                          }
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>

            {/* Adicionar membros */}
            {availableAgents && availableAgents.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Adicionar Agentes ({availableAgents.length} disponíveis)
                </Label>

                <ScrollArea className="h-[150px]">
                  <div className="space-y-2">
                    {availableAgents.map((agent) => (
                      <div
                        key={agent.user_id}
                        className="flex items-center justify-between p-2 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={agent.profile.avatar_url || undefined} />
                            <AvatarFallback>
                              {agent.profile.full_name?.charAt(0).toUpperCase() || 'U'}
                            </AvatarFallback>
                          </Avatar>

                          <div>
                            <p className="text-sm font-medium">{agent.profile.full_name}</p>
                            <p className="text-xs text-muted-foreground">{agent.profile.email}</p>
                          </div>
                        </div>

                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1.5"
                          onClick={() =>
                            selectedQueue &&
                            addMemberMutation.mutate({
                              queueId: selectedQueue.id,
                              userId: agent.user_id,
                            })
                          }
                        >
                          <UserPlus className="h-3.5 w-3.5" />
                          Adicionar
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMembersDialogOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
